import {
  Document,
  ChatMessage,
  ChatRequest,
  ChatResponse,
  UploadRequest,
  UploadResponse,
  KnowledgeBaseStats,
  ChatSession,
  DocumentStatus,
  ChunkingStrategy,
  ChunkingStrategyInfo,
  ChunkingRecommendation,
} from "../types/knowledge";
import { API_URL, ENDPOINTS } from "../../../foundation/altzero_base/base/utils/constants";
import {
  KNOWLEDGE_ENDPOINTS,
  KNOWLEDGE_TOAST_MESSAGES,
} from "../utils/constants";
import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";

// KB API URL: use dedicated env var if set, else fall back to the app's API_URL
const KB_API_URL = import.meta.env.VITE_KB_API_URL || API_URL;

// Graph features: 'true'/'memgraph' = Docker Memgraph, 'supabase' = Supabase graph tables, 'false' = disabled
const KB_GRAPH_FLAG = import.meta.env.VITE_KB_GRAPH_ENABLED || 'false';
const KB_GRAPH_ENABLED = KB_GRAPH_FLAG === 'true' || KB_GRAPH_FLAG === 'memgraph' || KB_GRAPH_FLAG === 'supabase';

class KnowledgeService {
  private baseUrl: string;
  private apiKey: string;
  private graphStatsCache: { data: { nodeCount: number; edgeCount: number; documentCount: number; entityTypes: Array<{ type: string; count: number }> }; ts: number } | null = null;
  private readonly GRAPH_STATS_TTL = 30_000; // 30s

  constructor() {
    this.baseUrl = KB_API_URL;
    this.apiKey = import.meta.env.VITE_API_KEY || "";
    console.log(`🔍 KnowledgeService baseUrl: ${this.baseUrl} | graph: ${KB_GRAPH_ENABLED ? 'enabled' : 'disabled'}`);
  }

  getApiUrl(): string {
    return this.baseUrl;
  }

  getApiKey(): string {
    return this.apiKey;
  }

  // Whether graph features (Graph Explorer tab, DEEP retrieval) are available
  isGraphEnabled(): boolean {
    return KB_GRAPH_ENABLED;
  }


  // Get current user ID from Supabase session
  private async getCurrentUserId(): Promise<string | null> {
    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.warn("Failed to get Supabase session:", error);
        return null;
      }

      return session?.user?.id || null;
    } catch (error) {
      console.warn("Failed to get user ID from Supabase:", error);
      return null;
    }
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    orgId?: string,
    userId?: string // Accept userId as parameter (from UserContext)
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    // Try to get userId from parameter first, then fallback to session
    let finalUserId = userId;
    if (!finalUserId) {
      finalUserId = await this.getCurrentUserId();
    }

    const defaultHeaders: Record<string, string> = {
      "x-api-key": this.apiKey,
    };

    // Add user ID to headers if available
    if (finalUserId) {
      defaultHeaders["x-user-id"] = finalUserId;
    }

    // Add org ID to headers if available (needed for Supabase RLS)
    if (orgId) {
      defaultHeaders["x-org-id"] = orgId;
    }

    // Only add Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      defaultHeaders["Content-Type"] = "application/json";
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    // Add userId to request body for POST/PUT requests if not FormData
    if (
      finalUserId &&
      (options.method === "POST" || options.method === "PUT") &&
      !(options.body instanceof FormData) &&
      options.body
    ) {
      try {
        const bodyData = JSON.parse(options.body as string);
        bodyData.userId = finalUserId;
        config.body = JSON.stringify(bodyData);
      } catch (error) {
        // If body is not JSON, just continue
      }
    }

    const isGraphCall = endpoint.includes('/graph/');
    if (isGraphCall) {
      console.log(`🔍 GRAPH REQUEST: ${endpoint}`);
      console.log(`🔍 GRAPH HEADERS:`, { 'x-user-id': defaultHeaders['x-user-id'], 'x-org-id': defaultHeaders['x-org-id'] });
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (isGraphCall) {
          console.error(`❌ GRAPH RESPONSE ERROR: ${response.status}`, errorData);
        }
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();
      if (isGraphCall) {
        console.log(`✅ GRAPH RESPONSE: ${endpoint}`, {
          nodeCount: data.nodes?.length ?? data.nodeCount ?? '–',
          edgeCount: data.edges?.length ?? data.edgeCount ?? '–',
          stats: data.stats || data,
        });
      }
      return data;
    } catch (error) {
      console.error(`❌ API request failed for ${endpoint}:`, error);
      console.error(`❌ Full URL: ${url}`);
      if (error instanceof Error) {
        console.error(`❌ Error type: ${error.constructor.name}`);
        console.error(`❌ Error message: ${error.message}`);
      }

      // Provide more specific error messages
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error(
          `Cannot connect to backend server at ${this.baseUrl}. ` +
          `Please ensure the backend server is running and accessible.`
        );
      }

      throw error;
    }
  }

  // Helper method to split file into chunks
  private splitFileIntoChunks(file: File, chunkSize: number = 1024 * 1024): Blob[] {
    const chunks: Blob[] = [];
    let start = 0;

    while (start < file.size) {
      const end = Math.min(start + chunkSize, file.size);
      chunks.push(file.slice(start, end));
      start = end;
    }

    return chunks;
  }

  // Helper method to upload a single file with chunking
  private async uploadSingleFileChunked(
    file: File,
    metadata: any,
    onProgress?: (progress: number, status: string, message: string) => void,
    orgId?: string
  ): Promise<any> {
    const CHUNK_SIZE = 1024 * 1024; // 1MB chunks
    const MAX_RETRIES = 3;
    const CHUNK_TIMEOUT = 30000; // 30 seconds per chunk
    const INITIATE_TIMEOUT = 10000; // 10 seconds for initiation
    const COMPLETE_TIMEOUT = 60000; // 60 seconds for completion

    // For small files (< 10MB), use regular upload
    if (file.size < 10 * 1024 * 1024) {
      return this.uploadSingleFileRegular(file, metadata, onProgress, orgId);
    }

    const chunks = this.splitFileIntoChunks(file, CHUNK_SIZE);
    const totalChunks = chunks.length;

    console.log(`📦 Uploading ${file.name} in ${totalChunks} chunks (${(file.size / 1024 / 1024).toFixed(2)}MB)`);

    // Step 1: Initiate chunked upload with timeout
    const initiateResponse = await Promise.race([
      this.makeRequest<{ sessionId: string }>(
        `${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}/upload/initiate`,
        {
          method: "POST",
          body: JSON.stringify({
            fileName: file.name,
            fileSize: file.size,
            totalChunks: totalChunks,
            metadata: metadata
          })
        },
        orgId
      ),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Upload initiation timeout")), INITIATE_TIMEOUT)
      )
    ]);

    const sessionId = initiateResponse.sessionId;
    console.log(`🚀 Initiated upload session: ${sessionId}`);

    // Step 2: Upload chunks with retry logic
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      let retryCount = 0;
      let chunkUploaded = false;

      while (!chunkUploaded && retryCount < MAX_RETRIES) {
        try {
          const chunkFormData = new FormData();
          chunkFormData.append("chunk", chunk);
          chunkFormData.append("sessionId", sessionId);
          chunkFormData.append("chunkIndex", chunkIndex.toString());

          const chunkHeaders: Record<string, string> = {
            "x-api-key": this.apiKey,
            "x-user-id": await this.getCurrentUserId() || "",
          };
          if (orgId) {
            chunkHeaders["x-org-id"] = orgId;
          }

          const chunkResponse = await Promise.race([
            fetch(
              `${this.baseUrl}${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}/upload/chunk`,
              {
                method: "POST",
                headers: chunkHeaders,
                body: chunkFormData,
              }
            ),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error("Chunk upload timeout")), CHUNK_TIMEOUT)
            )
          ]);

          if (!chunkResponse.ok) {
            throw new Error(`Chunk upload failed: ${chunkResponse.statusText}`);
          }

          const chunkResult = await chunkResponse.json();
          chunkUploaded = true;

          // Report real progress
          const progress = Math.round((chunkResult.totalUploaded / totalChunks) * 80); // 0-80% for upload
          onProgress?.(progress, "uploading", `Uploading ${file.name}... (${progress}%)`);

          console.log(`📦 Uploaded chunk ${chunkIndex + 1}/${totalChunks} for ${file.name} (${progress}%)`);

        } catch (error) {
          retryCount++;
          console.warn(`⚠️ Chunk ${chunkIndex} upload failed (attempt ${retryCount}/${MAX_RETRIES}):`, error);

          if (retryCount >= MAX_RETRIES) {
            throw new Error(`Failed to upload chunk ${chunkIndex} after ${MAX_RETRIES} attempts`);
          }

          // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
        }
      }
    }

    // Step 3: Complete upload and process file with timeout
    onProgress?.(85, "processing", `Processing ${file.name}...`);

    const completeResponse = await Promise.race([
      this.makeRequest<any>(
        `${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}/upload/complete`,
        {
          method: "POST",
          body: JSON.stringify({ sessionId })
        },
        orgId
      ),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Upload completion timeout")), COMPLETE_TIMEOUT)
      )
    ]);

    onProgress?.(100, "success", `${file.name} completed successfully`);
    return completeResponse;
  }

  // Helper method for regular upload (small files)
  private async uploadSingleFileRegular(
    file: File,
    metadata: any,
    onProgress?: (progress: number, status: string, message: string) => void,
    orgId?: string
  ): Promise<any> {
    const formData = new FormData();
    formData.append("files", file);
    formData.append("metadata", JSON.stringify(metadata));
    formData.append("userId", await this.getCurrentUserId() || "");

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 80);
          onProgress?.(progress, "uploading", `Uploading ${file.name}... (${progress}%)`);
        }
      });

      xhr.addEventListener("load", async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            // Simulate processing progress (80% to 100%)
            for (let progress = 80; progress <= 100; progress += 10) {
              const status = progress < 100 ? "processing" : "success";
              const message = progress < 100
                ? `Processing ${file.name}... (${progress}%)`
                : `${file.name} completed successfully`;

              onProgress?.(progress, status, message);

              if (progress < 100) {
                await new Promise(resolve => setTimeout(resolve, 200));
              }
            }

            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            reject(new Error("Failed to parse response"));
          }
        } else {
          reject(new Error(`Upload failed: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed"));
      });

      xhr.open("POST", `${this.baseUrl}${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}/upload`);
      xhr.setRequestHeader("x-api-key", this.apiKey);
      xhr.setRequestHeader("x-user-id", metadata.userId);
      if (orgId) {
        xhr.setRequestHeader("x-org-id", orgId);
      }
      xhr.send(formData);
    });
  }

  // Document Management - Upload to Knowledge Base backend
  async uploadDocuments(
    files: File[],
    onProgress?: (progressData: { [key: string]: any }) => void,
      additionalMetadata?: {
      provider?: 'knowledge' | 'gemini';
      storeId?: string; // For Gemini provider, pass the storeId
      companyId?: string;
      aiExtractionInstructions?: string; // This is "desc" or "docdesc"
      category?: string; // Sent separately, not in metadata
      subCategory?: string; // Stored in metadata JSON
      organisationId?: string;
      customTags?: string[];
      tags?: string[]; // KB tags stored in knowledge_docs.tags column
      companies?: any[]; // Array of company objects
      desc?: string; // Document description
      docdesc?: string; // Document description (alternative name)
      orgIds?: string; // JSON-stringified array of org IDs for multi-org scoping
      tier?: 'fast' | 'cost_effective' | 'agentic' | 'agentic_plus'; // LlamaParse tier
      language?: string[]; // OCR language(s) e.g. ['en']
      parsingInstructions?: string; // Custom parsing instruction for LlamaParse
      chunkingStrategy?: ChunkingStrategy; // Chunking strategy (default: 'fixed')
      chunkingConfig?: Record<string, unknown>; // Strategy-specific config
      pageIndex?: boolean; // Build page index (structural retrieval, opt-in)
      visibility?: 'private' | 'org' | 'global'; // Document visibility scope
      docNames?: string[]; // Per-file custom display names (parallel to files array)
      contextCategoryId?: string; // revisr_context_categories UUID — saved server-side to revisr_doc_category_map
    },
    orgId?: string,
    userId?: string // Accept userId as parameter (from UserContext)
  ): Promise<UploadResponse> {
    // Try to get userId from parameter first, then fallback to session
    let finalUserId = userId;
    if (!finalUserId) {
      finalUserId = await this.getCurrentUserId();
    }
    
    if (!finalUserId) {
      throw new Error(
        "User ID is required for document upload. Please log in."
      );
    }

    if (!orgId) {
      throw new Error(
        "Organization ID is required for document upload."
      );
    }

    // Generate a consistent storeId for this user/org
    // For Gemini provider, use the provided storeId (from Gemini store)
    // For Knowledge Base provider, auto-generate based on org/user
    const storeId = additionalMetadata?.storeId || `kb-${orgId.substring(0, 8)}-${finalUserId.substring(0, 8)}`;

    // Prepare metadata JSONB payload (stored in database metadata column)
    // This includes: desc, companies, docdesc, subCategory
    const metadataJson = {
      // Always include companyId, default to "default" if not provided
      companyId: additionalMetadata?.companyId || "default",
      // Document description (use desc or docdesc or aiExtractionInstructions)
      desc: additionalMetadata?.desc || additionalMetadata?.docdesc || additionalMetadata?.aiExtractionInstructions || undefined,
      docdesc: additionalMetadata?.docdesc || additionalMetadata?.desc || additionalMetadata?.aiExtractionInstructions || undefined,
      // Companies array if provided
      ...(additionalMetadata?.companies && { companies: additionalMetadata.companies }),
      // Sub-category
      ...(additionalMetadata?.subCategory && { subCategory: additionalMetadata.subCategory }),
      // Other optional fields
      ...(additionalMetadata?.organisationId && { organisationId: additionalMetadata.organisationId }),
      ...(additionalMetadata?.customTags && { customTags: additionalMetadata.customTags }),
      uploadedAt: new Date().toISOString(),
      source: "web_upload",
    };

    console.log("📦 Upload metadata:", JSON.stringify(metadataJson));
    console.log("📦 Category (sent separately):", additionalMetadata?.category || "general");

    // Generate consistent document IDs for progress tracking
    const uploadTimestamp = Date.now();
    const documentIds = files.map(
      (file, index) => `upload-${index}-${uploadTimestamp}`
    );

    const uploadedDocuments = [];
    const errors = [];

    // Upload files one by one to backend API
    for (let index = 0; index < files.length; index++) {
      const file = files[index];
      const documentId = documentIds[index];

      try {
        // Update progress: starting upload
        if (onProgress) {
          const progressData: { [key: string]: any } = {};
          progressData[documentId] = {
            progress: 0,
            status: "uploading",
            message: `Uploading ${file.name}...`,
            fileName: file.name,
          };
          // Set other files as pending
          files.forEach((f, i) => {
            if (i !== index) {
              const otherId = documentIds[i];
              if (i < index) {
                progressData[otherId] = {
                  progress: 100,
                  status: "success",
                  message: `${f.name} completed successfully`,
                  fileName: f.name,
                };
              } else {
                progressData[otherId] = {
                  progress: 0,
                  status: "pending",
                  message: `Waiting to upload ${f.name}...`,
                  fileName: f.name,
                };
              }
            }
          });
          onProgress(progressData);
        }

        // Create FormData matching backend API expectations
        const formData = new FormData();
        formData.append("file", file);
        // Use caller-supplied per-file name if provided and different from the raw filename
        const customDocName = additionalMetadata?.docNames?.[index];
        if (customDocName && customDocName.trim() && customDocName.trim() !== file.name) {
          formData.append("docName", customDocName.trim());
        }
        formData.append("storeId", storeId);
        formData.append("category", additionalMetadata?.category || "general");
        formData.append("metadata", JSON.stringify(metadataJson));
        formData.append("provider", additionalMetadata?.provider || "knowledge"); // Use selected provider
        // Always append orgIds — "[]" means no org (global), "[id1,id2]" means specific orgs
        if (additionalMetadata?.orgIds !== undefined) {
          formData.append("orgIds", additionalMetadata.orgIds);
        }
        // LlamaParse v2 options
        if (additionalMetadata?.tier) {
          formData.append("tier", additionalMetadata.tier);
        }
        if (additionalMetadata?.language?.length) {
          formData.append("language", JSON.stringify(additionalMetadata.language));
        }
        if (additionalMetadata?.parsingInstructions) {
          formData.append("parsingInstructions", additionalMetadata.parsingInstructions);
        }
        // Chunking strategy
        if (additionalMetadata?.chunkingStrategy) {
          formData.append("chunkingStrategy", additionalMetadata.chunkingStrategy);
        }
        if (additionalMetadata?.chunkingConfig) {
          formData.append("chunkingConfig", JSON.stringify(additionalMetadata.chunkingConfig));
        }
        if (additionalMetadata?.visibility) {
          formData.append("visibility", additionalMetadata.visibility);
        }
        if (additionalMetadata?.pageIndex) {
          formData.append("pageIndex", "true");
        }
        if (additionalMetadata?.tags?.length) {
          formData.append("tags", additionalMetadata.tags.join(","));
        }
        if (additionalMetadata?.contextCategoryId) {
          formData.append("contextCategoryId", additionalMetadata.contextCategoryId);
        }

        // Update progress: uploading (50%)
        if (onProgress) {
          const progressData: { [key: string]: any } = {};
          progressData[documentId] = {
            progress: 50,
            status: "uploading",
            message: `Uploading ${file.name}...`,
            fileName: file.name,
          };
          onProgress(progressData);
        }

        // Make request to backend
        const url = `${this.baseUrl}${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_UPLOAD}`;
        const headers: Record<string, string> = {
          "x-api-key": this.apiKey,
          "x-user-id": finalUserId,
        };
        if (orgId) {
          headers["x-org-id"] = orgId;
        }

        const response = await fetch(url, {
          method: "POST",
          headers,
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || `Upload failed: ${response.statusText}`
          );
        }

        // Update progress: processing (80%)
        if (onProgress) {
          const progressData: { [key: string]: any } = {};
          progressData[documentId] = {
            progress: 80,
            status: "processing",
            message: `Processing ${file.name}...`,
            fileName: file.name,
          };
          onProgress(progressData);
        }

        const result = await response.json();

        // Update progress: completed (100%)
        if (onProgress) {
          const progressData: { [key: string]: any } = {};
          progressData[documentId] = {
            progress: 100,
            status: "success",
            message: `${file.name} uploaded successfully`,
            fileName: file.name,
          };
          onProgress(progressData);
        }

        // Map backend response to frontend format
        uploadedDocuments.push({
          id: documentId,
          name: file.name,
          type: file.type || "application/octet-stream",
          uploadedAt: new Date().toISOString(),
          status: "success",
          size: file.size,
          visibility: additionalMetadata?.visibility || 'private',
          category: additionalMetadata?.category || 'general',
        });

      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        errors.push({
          fileName: file.name,
          error: error instanceof Error ? error.message : "Unknown error",
        });

        // Update progress to show error
        if (onProgress) {
          const progressData: { [key: string]: any } = {};
          progressData[documentId] = {
            progress: 0,
            status: "error",
            message: `Failed to upload ${file.name}`,
            fileName: file.name,
          };
          onProgress(progressData);
        }
      }
    }

    return {
      documents: uploadedDocuments,
      errors,
    };
  }

  // Search documents
  async searchDocuments(
    query: string,
    documentIds: string[] = [],
    options: {
      maxResults?: number;
      minRelevanceScore?: number;
    } = {}
  ): Promise<{
    results: Array<{
      id: string;
      text: string;
      score: number;
      metadata?: Record<string, any>;
    }>;
    sources: any[];
    totalResults: number;
  }> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error(
        "User ID is required for document search. Please log in."
      );
    }

    return this.makeRequest<{
      results: Array<{
        id: string;
        text: string;
        score: number;
        metadata?: Record<string, any>;
      }>;
      sources: any[];
      totalResults: number;
    }>(`${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_SEARCH}`, {
      method: "POST",
      body: JSON.stringify({
        query,
        documentIds,
        userId: userId,
        ...options,
      }),
    });
  }

  // Chat with Knowledge Base RAG (using /api/knowledge/rag/query endpoint)
  async chatWithRAG(request: ChatRequest, orgId?: string, userId?: string, provider?: 'knowledge' | 'gemini', storeId?: string): Promise<ChatResponse> {
    // Try to get userId from parameter first, then fallback to session
    let finalUserId = userId;
    if (!finalUserId) {
      finalUserId = await this.getCurrentUserId();
    }
    
    if (!finalUserId) {
      throw new Error("User ID is required for chat. Please log in.");
    }

    if (!orgId) {
      throw new Error("Organization ID is required for chat.");
    }

    console.log(`🔍 FRONTEND - chatWithRAG called with:`);
    console.log(`   Message: "${request.message}"`);
    console.log(`   SelectedDocuments:`, request.selectedDocuments);
    console.log(`   UserId: ${finalUserId}`);
    console.log(`   OrgId: ${orgId}`);

    // Use the RAG query endpoint instead of chat endpoint
    // Map selectedDocuments (which are now document names) to metadata filter if provided
    // The backend will filter results by fileName matching these document names
    const metadataFilter = request.selectedDocuments && request.selectedDocuments.length > 0
      ? { documentIds: request.selectedDocuments }
      : undefined;

    const response = await this.makeRequest<{
      answer: string;
      retrieval?: string;
      thinking?: string;
      sources: Array<{
        // Standard RAG source shape
        id?: string;
        content?: string;
        score?: number;
        metadata?: Record<string, any>;
        // Page-index source shape
        nodeId?: string;
        docId?: string;
        documentTitle?: string;
        sectionTitle?: string;
        pageRange?: [number, number];
      }>;
      metadata?: {
        retrievedDocuments: number;
        processingTime: number;
        model: string;
        hasValidContext: boolean;
        ragChunks?: number;
        pageIndexSections?: number;
        graphContext?: {
          entitiesFound: number;
          relationshipsTraversed: number;
          graphChunksAdded: number;
        };
      };
      goldenMatch?: {
        id: string;
        similarity: number;
        question: string;
        confidence: number;
      };
    }>("/api/knowledge/rag/query", {
      method: "POST",
      body: JSON.stringify({
        query: request.message,
        systemMessage: request.systemMessage,
        userId: finalUserId,
        orgId: orgId,
        category: request.category,
        metadata: metadataFilter,
        storeId: storeId, // Pass storeId for Gemini provider
        // page-index and deep are top-level retrieval modes (not vector search modes)
        retrieval: (request.searchMode === 'page-index' || request.searchMode === 'deep')
          ? request.searchMode
          : 'standard',
        options: {
          temperature: request.temperature,
          maxTokens: request.maxTokens,
          // For structural modes, use hybrid as the fallback vector mode
          searchMode: (request.searchMode === 'page-index' || request.searchMode === 'deep')
            ? 'hybrid'
            : request.searchMode,
          topK: request.topK,
          minScore: request.minScore,
        },
        provider: provider || "knowledge", // Use selected provider
      }),
    }, orgId, finalUserId);

    // Map response to ChatResponse format — handles both standard RAG and page-index source shapes
    return {
      message: response.answer,
      sources: (response.sources || []).map((source) => {
        // Page-index source shape: {nodeId, docId, documentTitle, sectionTitle, pageRange}
        if (source.nodeId || source.docId) {
          const excerpt = source.sectionTitle || "Section";
          return {
            documentId: source.docId || source.nodeId || "",
            documentName: source.documentTitle || "Unknown Document",
            pageNumber: source.pageRange?.[0],
            relevanceScore: 1,
            excerpt,
          };
        }
        // Standard RAG source shape: {id, content, score, metadata}
        const content = source.content || "";
        return {
          documentId: source.id || "",
          documentName: source.metadata?.fileName || source.metadata?.filename || "Unknown Document",
          pageNumber: source.metadata?.pageNumber,
          relevanceScore: source.score ?? 0,
          excerpt: content.length > 200 ? content.substring(0, 200) + "..." : content,
        };
      }),
      sessionId: `session-${Date.now()}`,
      metadata: {
        model: response.metadata?.model || "",
        processingTime: response.metadata?.processingTime || 0,
        retrievalResults: response.metadata?.retrievedDocuments || response.sources?.length || 0,
      },
      retrieval: response.retrieval || (request.searchMode === 'page-index' || request.searchMode === 'deep' ? request.searchMode : undefined),
      goldenMatch: response.goldenMatch || undefined,
      graphContext: response.metadata?.graphContext || undefined,
      ragChunks: response.metadata?.ragChunks,
      pageIndexSections: response.metadata?.pageIndexSections,
    };
  }

  // Streaming RAG chat (standard vector mode only — page-index/deep/gemini use chatWithRAG)
  async streamChatWithRAG(
    request: ChatRequest,
    orgId: string,
    userId: string,
    onToken: (token: string) => void,
    onSources: (sources: string[]) => void,
    onDone: (meta: Record<string, unknown>) => void,
    onError: (error: string) => void
  ): Promise<void> {
    const metadataFilter = request.selectedDocuments && request.selectedDocuments.length > 0
      ? { documentIds: request.selectedDocuments }
      : undefined;

    const response = await fetch(`${KB_API_URL}${KNOWLEDGE_ENDPOINTS.RAG_STREAM}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": this.apiKey,
        "x-user-id": userId,
        "x-org-id": orgId,
      },
      body: JSON.stringify({
        query: request.message,
        systemMessage: request.systemMessage,
        userId,
        orgId,
        category: request.category,
        metadata: metadataFilter,
        options: {
          temperature: request.temperature,
          maxTokens: request.maxTokens,
          searchMode: request.searchMode || 'semantic',
          topK: request.topK,
          minScore: request.minScore,
        },
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      onError((err.message || err.error || `HTTP ${response.status}`) as string);
      return;
    }

    if (!response.body) { onError("No response body"); return; }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        let eventType = "";
        for (const line of lines) {
          if (line.startsWith("event: ")) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            const data = line.slice(6);
            try {
              if (eventType === "token") onToken(JSON.parse(data) as string);
              else if (eventType === "sources") onSources(JSON.parse(data) as string[]);
              else if (eventType === "done") onDone(JSON.parse(data) as Record<string, unknown>);
              else if (eventType === "error") onError(JSON.parse(data) as string);
            } catch { /* ignore malformed chunks */ }
            eventType = "";
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  // Document Management
  async getDocuments(companyId?: string, includeDefault?: boolean, orgId?: string, userId?: string, filterOrgIds?: string[]): Promise<Document[]> {
    // Try to get userId from parameter first, then fallback to session
    let finalUserId = userId;
    if (!finalUserId) {
      finalUserId = await this.getCurrentUserId();
    }

    if (!finalUserId) {
      throw new Error("User ID is required to get documents. Please log in.");
    }

    let url = `${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}?userId=${finalUserId}`;
    if (companyId) {
      url += `&companyId=${encodeURIComponent(companyId)}`;
      if (includeDefault) {
        url += `&includeDefault=true`;
      }
    }
    // Pass org IDs filter for multi-org scoping
    if (filterOrgIds && filterOrgIds.length > 0) {
      url += `&orgIds=${encodeURIComponent(JSON.stringify(filterOrgIds))}`;
    }

    return this.makeRequest<Document[]>(url, {}, orgId, finalUserId);
  }

  // Get ALL documents (for client-side filtering)
  async getAllDocuments(): Promise<Document[]> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error("User ID is required to get documents. Please log in.");
    }

    const url = `${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}?userId=${userId}&all=true`;
    return this.makeRequest<Document[]>(url);
  }

  // Delete document
  async deleteDocument(documentId: string, orgId?: string, userId?: string): Promise<void> {
    // Try to get userId from parameter first, then fallback to session
    let finalUserId = userId;
    if (!finalUserId) {
      finalUserId = await this.getCurrentUserId();
    }
    
    if (!finalUserId) {
      throw new Error("User ID is required to delete document. Please log in.");
    }

    // Use URL path approach like Quick Compare does - encode documentId for URL
    // The backend will decode it using decodeURIComponent
    const encodedDocumentId = encodeURIComponent(documentId);
    
    return this.makeRequest<void>(
      `${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}/${encodedDocumentId}?userId=${finalUserId}`,
      {
        method: "DELETE",
      },
      orgId,
      finalUserId
    );
  }

  // Bulk delete documents
  async deleteDocuments(documentIds: string[]): Promise<{
    deletedCount: number;
    deletedDocuments: Array<{ id: string; name: string }>;
  }> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error(
        "User ID is required to delete documents. Please log in."
      );
    }

    if (!documentIds || documentIds.length === 0) {
      throw new Error(
        "At least one document ID is required for bulk deletion."
      );
    }

    return this.makeRequest<{
      deletedCount: number;
      deletedDocuments: Array<{ id: string; name: string }>;
    }>(`${KNOWLEDGE_ENDPOINTS.KNOWLEDGE_DOCUMENTS}?userId=${userId}`, {
      method: "DELETE",
      body: JSON.stringify({ documentIds }),
    });
  }

  // Chat Management
  async sendChatMessage(request: ChatRequest): Promise<ChatResponse> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error("User ID is required for chat. Please log in.");
    }

    return this.makeRequest<ChatResponse>(KNOWLEDGE_ENDPOINTS.CHAT, {
      method: "POST",
      body: JSON.stringify({
        ...request,
        userId: userId,
      }),
    });
  }

  async streamChatMessage(
    request: ChatRequest,
    onChunk: (chunk: string) => void,
    onComplete: (response: ChatResponse) => void,
    onError: (error: Error) => void
  ): Promise<void> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      onError(
        new Error("User ID is required for chat streaming. Please log in.")
      );
      return;
    }

    try {
      const response = await fetch(
        `${this.baseUrl}${KNOWLEDGE_ENDPOINTS.CHAT_STREAM}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": this.apiKey,
            "x-user-id": userId,
          },
          body: JSON.stringify({
            ...request,
            userId: userId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Response body is not readable");
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let fullResponse = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);

            if (data === "[DONE]") {
              return;
            }

            try {
              const chunk = JSON.parse(data);

              if (chunk.error) {
                onError(new Error(chunk.error));
                return;
              }

              if (chunk.chunk) {
                fullResponse += chunk.chunk;
                onChunk(chunk.chunk);
              }

              if (chunk.complete) {
                onComplete({
                  message: chunk.message || fullResponse,
                  sources: chunk.sources || [],
                  sessionId: request.sessionId || "",
                  metadata: chunk.metadata || {},
                });
                return;
              }
            } catch (e) {
              // Ignore malformed chunks
            }
          }
        }
      }
    } catch (error) {
      onError(
        error instanceof Error ? error : new Error("Unknown streaming error")
      );
    }
  }

  async getChatSessions(): Promise<ChatSession[]> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error(
        "User ID is required to get chat sessions. Please log in."
      );
    }

    return this.makeRequest<ChatSession[]>(
      `${KNOWLEDGE_ENDPOINTS.CHAT_HISTORY}?userId=${userId}`
    );
  }

  async createChatSession(
    title: string,
    selectedDocuments: string[]
  ): Promise<ChatSession> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error(
        "User ID is required to create chat session. Please log in."
      );
    }

    return this.makeRequest<ChatSession>(KNOWLEDGE_ENDPOINTS.CHAT_HISTORY, {
      method: "POST",
      body: JSON.stringify({
        title,
        selectedDocuments,
        userId: userId,
      }),
    });
  }

  async updateChatSession(
    sessionId: string,
    updates: Partial<ChatSession>
  ): Promise<ChatSession> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error(
        "User ID is required to update chat session. Please log in."
      );
    }

    return this.makeRequest<ChatSession>(
      `${KNOWLEDGE_ENDPOINTS.CHAT_HISTORY}/${sessionId}?userId=${userId}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          ...updates,
          userId: userId,
        }),
      }
    );
  }

  async deleteChatSession(sessionId: string): Promise<void> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error(
        "User ID is required to delete chat session. Please log in."
      );
    }

    return this.makeRequest<void>(
      `${KNOWLEDGE_ENDPOINTS.CHAT_HISTORY}/${sessionId}?userId=${userId}`,
      {
        method: "DELETE",
      }
    );
  }

  // Utility methods
  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  getFileIcon(fileType: string): string {
    const type = fileType.toLowerCase();

    if (type.includes("pdf")) return "📄";
    if (type.includes("word") || type.includes("doc")) return "📝";
    if (type.includes("text") || type.includes("txt")) return "📃";
    if (type.includes("markdown") || type.includes("md")) return "📋";

    return "📄";
  }

  validateFile(file: File): { isValid: boolean; error?: string } {
    const maxSize = 50 * 1024 * 1024; // 50MB
    const allowedTypes = [
      // PDF files
      "application/pdf",

      // Word documents
      "application/msword", // .doc
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx

      // Text files
      "text/plain", // .txt

      // Markdown files (multiple MIME types for compatibility)
      "text/markdown", // .md
      "text/x-markdown", // .md (alternative)
      "application/x-markdown", // .md (alternative)

      // RTF files
      "application/rtf",

      // OpenDocument Text
      "application/vnd.oasis.opendocument.text", // .odt
    ];

    // Check file size
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `File size exceeds 500MB limit. Current size: ${this.formatFileSize(
          file.size
        )}`,
      };
    }

    // Get file extension for additional validation
    const fileExtension = file.name.toLowerCase().split('.').pop();
    const allowedExtensions = ['pdf', 'doc', 'docx', 'txt', 'md', 'markdown', 'rtf', 'odt'];

    console.log(`📄 Frontend file validation - Name: ${file.name}, MIME: ${file.type}, Extension: ${fileExtension}`);

    // Check both MIME type and file extension for better compatibility
    const isMimeTypeAllowed = allowedTypes.includes(file.type);
    const isExtensionAllowed = allowedExtensions.includes(fileExtension || '');

    if (!isMimeTypeAllowed && !isExtensionAllowed) {
      return {
        isValid: false,
        error: `File type not supported. Supported formats: PDF, Word (.doc/.docx), Text (.txt), Markdown (.md), RTF, ODT. Received: ${file.type} (${fileExtension})`,
      };
    }

    console.log(`✅ Frontend file validation passed: ${file.name}`);
    return { isValid: true };
  }

  generateSessionTitle(firstMessage: string): string {
    // Generate a title from the first message (max 50 chars)
    const title = firstMessage.trim().slice(0, 50);
    return title.length < firstMessage.trim().length ? `${title}...` : title;
  }

  // Graph Explorer Methods
  async getGraphSubgraph(params: {
    docName?: string;
    entityType?: string;
    search?: string;
    limit?: number;
  }, orgId?: string, userId?: string): Promise<{
    nodes: Array<{ id: string; label: string; type: string; entityType?: string; properties?: Record<string, string> }>;
    edges: Array<{ source: string; target: string; label: string; context?: string }>;
    stats: { totalEntities: number; totalRelationships: number; returnedNodes: number; returnedEdges: number };
  }> {
    const query = new URLSearchParams();
    if (params.docName) query.set('docName', params.docName);
    if (params.entityType) query.set('entityType', params.entityType);
    if (params.search) query.set('search', params.search);
    if (params.limit) query.set('limit', String(params.limit));

    return this.makeRequest(`/api/knowledge/graph/subgraph?${query.toString()}`, {}, orgId, userId);
  }

  async getGraphStats(orgId?: string, userId?: string): Promise<{
    nodeCount: number;
    edgeCount: number;
    documentCount: number;
    entityTypes: Array<{ type: string; count: number }>;
  }> {
    const now = Date.now();
    if (this.graphStatsCache && now - this.graphStatsCache.ts < this.GRAPH_STATS_TTL) {
      return this.graphStatsCache.data;
    }
    const data = await this.makeRequest<{ nodeCount: number; edgeCount: number; documentCount: number; entityTypes: Array<{ type: string; count: number }> }>('/api/knowledge/graph/stats', {}, orgId, userId);
    this.graphStatsCache = { data, ts: now };
    return data;
  }

  invalidateGraphStatsCache(): void {
    this.graphStatsCache = null;
  }

  async getEntityTypes(orgId?: string): Promise<string[]> {
    const result = await this.makeRequest<{ types: string[] }>('/api/knowledge/graph/entity-types', {}, orgId);
    return result.types;
  }

  async getEntityDetail(entityName: string, orgId?: string): Promise<{
    name: string;
    type: string;
    properties: Record<string, string>;
    relationships: Array<{ direction: 'in' | 'out'; type: string; targetEntity: string }>;
  }> {
    return this.makeRequest(`/api/knowledge/graph/entities/${encodeURIComponent(entityName)}`, {}, orgId);
  }

  // Chunking Strategy Methods
  async getChunkingStrategies(): Promise<ChunkingStrategyInfo[]> {
    const result = await this.makeRequest<{ strategies: ChunkingStrategyInfo[] }>(
      '/api/knowledge/chunking/strategies'
    );
    return result.strategies;
  }

  async getChunkingRecommendation(
    sampleText: string,
    fileName: string,
    category?: string
  ): Promise<ChunkingRecommendation> {
    return this.makeRequest<ChunkingRecommendation>(
      '/api/knowledge/chunking/recommend',
      {
        method: 'POST',
        body: JSON.stringify({ sampleText, fileName, category }),
      }
    );
  }

  // Full document analysis — chunking + category + tags + ontology in one AI call
  async analyzeDocument(
    sampleText: string,
    fileName: string,
    category?: string
  ): Promise<import('../types/knowledge').DocumentAnalysis> {
    return this.makeRequest<import('../types/knowledge').DocumentAnalysis>(
      '/api/knowledge/analyze',
      {
        method: 'POST',
        body: JSON.stringify({ sampleText, fileName, category }),
      }
    );
  }

  // Get available managed ontologies from DB
  async getManagedOntologies(): Promise<{
    ontologies: Array<{
      id: string;
      name: string;
      domain: string;
      description: string;
      version: string;
      isDefault: boolean;
      source: string;
      entityTypeCount: number;
      relationshipTypeCount: number;
      entityTypes: Array<{ name: string; description: string; properties: string[] }>;
      relationshipTypes: Array<{ name: string; fromType: string; toType: string; description: string }>;
    }>;
    mode: string;
  }> {
    return this.makeRequest('/api/knowledge/ontologies');
  }

  // Alias for backward compat
  async getStaticOntologies() {
    return this.getManagedOntologies();
  }

  // Save/update a managed ontology
  async saveOntology(ontology: {
    id?: string;
    domain: string;
    name: string;
    description?: string;
    isDefault?: boolean;
    entityTypes: Array<{ name: string; description: string; properties: string[] }>;
    relationshipTypes: Array<{ name: string; fromType: string; toType: string; description: string }>;
  }): Promise<{ success: boolean; id: string }> {
    if (ontology.id) {
      return this.makeRequest(`/api/knowledge/ontologies/${ontology.id}`, {
        method: 'PUT',
        body: JSON.stringify(ontology),
      });
    }
    return this.makeRequest('/api/knowledge/ontologies', {
      method: 'POST',
      body: JSON.stringify(ontology),
    });
  }

  // Expand an ontology with new types
  async expandOntology(id: string, entityTypes: Array<{ name: string; description: string; properties: string[] }>, relationshipTypes: Array<{ name: string; fromType: string; toType: string; description: string }>): Promise<{ success: boolean; addedEntities: number; addedRels: number }> {
    return this.makeRequest(`/api/knowledge/ontologies/${id}/expand`, {
      method: 'POST',
      body: JSON.stringify({ entityTypes, relationshipTypes }),
    });
  }

  // Delete a managed ontology
  async deleteOntology(id: string): Promise<{ success: boolean }> {
    return this.makeRequest(`/api/knowledge/ontologies/${id}`, { method: 'DELETE' });
  }

  // ─── Category CRUD ──────────────────────────────────────────

  async getCategories(): Promise<{ categories: import('../types/knowledge').Category[] }> {
    return this.makeRequest('/api/knowledge/categories');
  }

  async createCategory(data: { name: string; displayName: string; description?: string; subcategories?: string[] }): Promise<{ success: boolean; id: string }> {
    return this.makeRequest('/api/knowledge/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategory(id: string, data: { displayName?: string; description?: string; subcategories?: string[] }): Promise<{ success: boolean }> {
    return this.makeRequest(`/api/knowledge/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(id: string): Promise<{ success: boolean }> {
    return this.makeRequest(`/api/knowledge/categories/${id}`, { method: 'DELETE' });
  }

  // ─── Tag CRUD ───────────────────────────────────────────────

  async getTags(): Promise<{ tags: import('../types/knowledge').Tag[] }> {
    return this.makeRequest('/api/knowledge/tags');
  }

  async createTag(name: string): Promise<{ success: boolean; id: string }> {
    return this.makeRequest('/api/knowledge/tags', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async renameTag(id: string, name: string): Promise<{ success: boolean }> {
    return this.makeRequest(`/api/knowledge/tags/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  }

  async deleteTag(id: string): Promise<{ success: boolean }> {
    return this.makeRequest(`/api/knowledge/tags/${id}`, { method: 'DELETE' });
  }

  // Enrich graph — AI discovers cross-document entity relationships
  // Step 1: preview suggestions (dryRun=true)
  async enrichGraphPreview(ontologyId?: string, orgId?: string, userId?: string, focusEntity?: string, aiComment?: string): Promise<{
    success: boolean;
    dryRun: boolean;
    newRelationships: number;
    totalEntities: number;
    standaloneEntities: number;
    suggestions: Array<{ from: string; to: string; type: string; context: string }>;
  }> {
    return this.makeRequest('/api/knowledge/graph/enrich', {
      method: 'POST',
      body: JSON.stringify({ ontologyId, dryRun: true, focusEntity, aiComment }),
    }, orgId, userId);
  }

  // Step 2: apply selected suggestions
  async enrichGraphApply(selectedSuggestions: Array<{ from: string; to: string; type: string }>, orgId?: string, userId?: string): Promise<{
    success: boolean;
    newRelationships: number;
  }> {
    return this.makeRequest('/api/knowledge/graph/enrich', {
      method: 'POST',
      body: JSON.stringify({ dryRun: false, selectedSuggestions }),
    }, orgId, userId);
  }

  async deleteGraphRelationships(
    relationships: Array<{ from: string; to: string; type: string }>,
    orgId?: string,
    userId?: string,
  ): Promise<{ success: boolean; deleted: number }> {
    return this.makeRequest('/api/knowledge/graph/relationships', {
      method: 'DELETE',
      body: JSON.stringify({ relationships }),
    }, orgId, userId);
  }

  // Reprocess graph entities for a document (uses existing chunks)
  async reprocessGraph(docName: string, forceNewOntology = false, category?: string): Promise<{
    success: boolean;
    entities: number;
    relationships: number;
    error?: string;
  }> {
    return this.makeRequest('/api/knowledge/graph/reprocess', {
      method: 'POST',
      body: JSON.stringify({ docName, forceNewOntology, category: category || undefined }),
    });
  }

  async retryPageIndex(docName: string, orgId?: string, userId?: string): Promise<{ success: boolean; message?: string; error?: string }> {
    return this.makeRequest('/api/knowledge/documents/retry-page-index', {
      method: 'POST',
      body: JSON.stringify({ docName }),
    }, orgId, userId);
  }

  async updateDocument(docName: string, updates: { category?: string; tags?: string[]; name?: string; visibility?: 'private' | 'org' | 'global'; resetGraphStatus?: boolean; resetPageIndexStatus?: boolean }, orgId?: string, userId?: string): Promise<{ success: boolean; error?: string }> {
    return this.makeRequest(`/api/knowledge/documents/${encodeURIComponent(docName)}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }, orgId, userId);
  }

  async getEntityDetails(entityName: string, orgId?: string, userId?: string): Promise<{
    entity: string;
    type: string;
    properties: Record<string, unknown>;
    sources: Array<{
      docId: string;
      docName: string;
      category: string;
      chunks: Array<{ chunkIndex: number; pageNumber?: number; excerpt: string }>;
    }>;
  }> {
    return this.makeRequest(
      `/api/knowledge/graph/entity-details?name=${encodeURIComponent(entityName)}`,
      {},
      orgId,
      userId
    );
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.makeRequest<{ status: string; timestamp: string }>(
      ENDPOINTS.HEALTH
    );
  }

  // Get document summary using LangGraph analysis
  async getDocumentSummary(documentId: string): Promise<{
    content: string;
    metadata: any;
  }> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error(
        "User ID is required to get document summary. Please log in."
      );
    }

    return this.makeRequest<{
      content: string;
      metadata: any;
    }>(`/api/knowledge/summary/${documentId}?userId=${userId}`, {
      method: "GET",
    });
  }
  // ─── User Role ──────────────────────────────────────────────

  async getUserRole(orgId?: string, userId?: string): Promise<{ isOrgAdmin: boolean; isPlatformAdmin: boolean }> {
    return this.makeRequest('/api/knowledge/user-role', {}, orgId, userId);
  }

  // ─── Golden Q&A ─────────────────────────────────────────────

  async getGoldenEntries(orgId?: string, userId?: string, category?: string): Promise<{ entries: any[]; total: number }> {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    const qs = params.toString() ? `?${params}` : '';
    return this.makeRequest(`${KNOWLEDGE_ENDPOINTS.GOLDEN_LIST}${qs}`, {}, orgId, userId);
  }

  async createGoldenEntry(data: {
    question: string;
    questionVariants?: string[];
    answer: string;
    sources?: Array<{ documentName: string; sectionTitle?: string; pageRange?: string }>;
    category?: string;
    tags?: string[];
    visibility?: 'private' | 'org' | 'global';
    confidence?: number;
    matchThreshold?: number;
  }, orgId?: string, userId?: string): Promise<{ success: boolean; entry: any }> {
    return this.makeRequest(KNOWLEDGE_ENDPOINTS.GOLDEN_CREATE, {
      method: 'POST',
      body: JSON.stringify(data),
    }, orgId, userId);
  }

  async updateGoldenEntry(id: string, data: Record<string, unknown>, orgId?: string, userId?: string): Promise<{ success: boolean; entry: any }> {
    return this.makeRequest(`${KNOWLEDGE_ENDPOINTS.GOLDEN_UPDATE}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, orgId, userId);
  }

  async deleteGoldenEntry(id: string): Promise<{ success: boolean }> {
    return this.makeRequest(`${KNOWLEDGE_ENDPOINTS.GOLDEN_DELETE}/${id}`, { method: 'DELETE' });
  }

  async testGoldenMatch(query: string, orgId?: string, userId?: string): Promise<{ results: any[]; tier: string }> {
    return this.makeRequest(KNOWLEDGE_ENDPOINTS.GOLDEN_TEST, {
      method: 'POST',
      body: JSON.stringify({ query }),
    }, orgId, userId);
  }

  async importGoldenEntries(entries: any[], orgId?: string, userId?: string): Promise<{ success: boolean; imported: number; errors: string[] }> {
    return this.makeRequest(KNOWLEDGE_ENDPOINTS.GOLDEN_IMPORT, {
      method: 'POST',
      body: JSON.stringify({ entries }),
    }, orgId, userId);
  }

  async getGoldenStats(orgId?: string): Promise<{ totalEntries: number; activeEntries: number; totalHits: number; topEntries: any[] }> {
    return this.makeRequest(KNOWLEDGE_ENDPOINTS.GOLDEN_STATS, {}, orgId);
  }
}

export const knowledgeService = new KnowledgeService();
